import { Megaphone } from 'lucide-react';
import type { PaginatedSalesResponseDto } from '@/generated/korza-api-client/types.gen';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  firstProductLabel,
  formatSaleAmount,
  formatSaleDateTime,
  formatSaleStatus,
} from '@/lib/sales-history';
import { cn } from '@/lib/utils';

export type SalesHistorySale = PaginatedSalesResponseDto['data'][number];

interface SaleCardProps {
  sale: SalesHistorySale;
  onOpen: () => void;
}

/** One order of the mobile sales history (SALES-07 AC 2); opens the detail sheet. */
export function SaleCard({ sale, onOpen }: SaleCardProps) {
  const { LL, locale } = useI18nContext();
  const product = firstProductLabel(sale.lineItemNames);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-h-touch w-full flex-col gap-1 rounded-mobile-card border bg-card p-4 text-left text-card-foreground outline-none transition-colors active:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex w-full items-baseline justify-between gap-3">
        <span className="font-semibold">{sale.orderNumber}</span>
        <span className="font-semibold tabular-nums">
          {formatSaleAmount(sale, locale)}
        </span>
      </span>
      <span className="flex w-full items-baseline justify-between gap-3 text-sm text-muted-foreground">
        <span>{formatSaleDateTime(sale.date, locale)}</span>
        <span
          className={cn(
            sale.paymentStatus === 'paid' ? 'text-foreground' : undefined
          )}
        >
          {formatSaleStatus(sale.paymentStatus, LL)}
        </span>
      </span>
      {product && (
        <span className="truncate text-sm">
          {product.extra > 0
            ? LL.salesPage.mobile.moreProducts({
                first: product.first,
                count: product.extra,
              })
            : product.first}
        </span>
      )}
      {sale.campaignName && (
        <span
          data-sale-campaign=""
          className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground"
        >
          <Megaphone className="size-3.5 shrink-0" aria-hidden />
          <span className="truncate">{sale.campaignName}</span>
        </span>
      )}
    </button>
  );
}
